import { Stack, styled } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import { SALE_DISCOUNT_PERCENT } from '../../Common/_hooks/useIsSaleProduct';
import ProductCatalogRewardBadge from './ProductCatalogRewardBadge';

interface ProductCatalogPricingProps {
  isOnSale: boolean
  product: ViewProductStore
}

const ProductPrice = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
}));

const ProductPriceAmount = styled('div')(({ theme }) => ({
  color: '#333333',
  fontSize: 19,
  fontWeight: 500,
  lineHeight: 1,

  '&.sale': {
    color: theme.palette.accent.main,
  },
}));

const ProductRegularPrice = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'line-through',
  textDecorationThickness: '1.5px',
  whiteSpace: 'nowrap',
}));

const WholesalePrice = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: 12,
  fontWeight: 600,
  gap: theme.spacing(0.75),
  lineHeight: 1.2,
}));

function ProductCatalogPricing(props: ProductCatalogPricingProps) {
  const [t] = useTranslation('products');
  const rewardId = props.product.get('id_reward');
  const reward = useAppSelector(state => (
    rewardId === null
      ? undefined
      : state.get('appData').get('rewards').get(String(rewardId))
  ));
  const rewardUsed = useAppSelector(state => (
    rewardId !== null
    && state.get('userData').get('rewards').some(userReward => (
      userReward.get('id_reward') === rewardId
      && userReward.get('used')
    ))
  ));
  const regularPrice = props.product.get('price').toDinero();
  const startingWholesalePrice = props.product.get('starting_wholesale_price');
  const startingSetPrice = props.product.get('starting_set_price');
  const startingSetQuantity = props.product.get('starting_set_quantity');
  const salePrice = props.isOnSale
    ? regularPrice.percentage(100 - SALE_DISCOUNT_PERCENT)
    : null;
  const regularPriceDisplay = regularPrice.toFormat();
  const displayPrice = salePrice?.toFormat() ?? regularPriceDisplay;
  const wholesalePriceDisplay = startingWholesalePrice?.toDinero().toFormat() ?? null;
  const setPriceDisplay = wholesalePriceDisplay === null
    ? startingSetPrice?.toDinero().toFormat() ?? null
    : null;

  return <Stack spacing={0.25}>
    <ProductPrice>
      {salePrice !== null ? <ProductRegularPrice>{regularPriceDisplay}</ProductRegularPrice> : null}
      <ProductPriceAmount
        className={salePrice !== null ? 'sale' : undefined}
      >{displayPrice}</ProductPriceAmount>
      {salePrice === null && !rewardUsed ? <ProductCatalogRewardBadge reward={reward} /> : null}
    </ProductPrice>
    {wholesalePriceDisplay !== null ? <WholesalePrice>
      <span>{t('Wholesale from {{price}}', { price: wholesalePriceDisplay })}</span>
    </WholesalePrice> : null}
    {setPriceDisplay !== null && startingSetQuantity !== null ? <WholesalePrice>
      <span>{t('{{price}} each in sets of {{quantity}}', { price: setPriceDisplay, quantity: startingSetQuantity })}</span>
    </WholesalePrice> : null}
  </Stack>;
}

export default memo(ProductCatalogPricing);
